import React, { useEffect, useState } from "react";
import { ErrorNotify } from "../../shared/Alert";
import { getInspectionCycleActivityList } from "../../helpers/backend_helper";

const formatActivityDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const InspectionCycleHistory = ({ dataId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadActivities = () => {
    if (!dataId) return;

    setLoading(true);
    getInspectionCycleActivityList(dataId)
      .then((response) => {
        if (response?.status) {
          setActivities(Array.isArray(response.data) ? response.data : []);
        } else {
          setActivities([]);
          ErrorNotify(
            response?.message || "Unable to load inspection cycle activities."
          );
        }
      })
      .catch(() => {
        setActivities([]);
        ErrorNotify("Unable to load inspection cycle activities.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadActivities();
  }, [dataId]);

  return (
    <section
      className="inspection-cycle-activity"
      aria-label="Inspection cycle activity timeline"
    >
      <header className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-1">Activity timeline</h4>
          <p className="text-muted mb-0">
            User actions and system-recorded events for this inspection cycle.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-light btn-sm piers-refresh-button"
          onClick={loadActivities}
          disabled={loading}
        >
          <i className="bx bx-refresh me-1" aria-hidden="true" />{" "}
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </header>

      {loading && activities.length === 0 ? (
        <div className="text-muted py-3">Loading activities...</div>
      ) : activities.length === 0 ? (
        <div className="border rounded p-4 text-center text-muted">
          No activity has been recorded for this inspection cycle yet.
        </div>
      ) : (
        <div className="border rounded">
          {activities.map((activity) => {
            const actor =
              activity.userProfile?.fullName ||
              (activity.activityBy ? "User" : "System");
            const isSystemEvent =
              /^Inspection cycle (created|status changed|archived|progress updated|update recorded)/i.test(
                activity.activityNote || ""
              );
            const actorType =
              isSystemEvent || !activity.activityBy
                ? "System activity"
                : "User activity";
            return (
              <article
                key={activity.id}
                className="d-flex gap-3 p-3 border-bottom"
              >
                <span
                  className="rounded-circle bg-light text-primary d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 32, height: 32 }}
                >
                  <i
                    className={
                      actorType === "System activity"
                        ? "bx bx-cog"
                        : "bx bx-user"
                    }
                    aria-hidden="true"
                  />
                </span>
                <div className="flex-grow-1">
                  <div className="fw-medium">
                    {activity.activityNote ||
                      "Inspection cycle activity recorded."}
                  </div>
                  <small className="text-muted">
                    {actor} · {actorType}
                  </small>
                </div>
                <time className="text-muted small text-nowrap">
                  {formatActivityDate(
                    activity.activityDate || activity.createdOn
                  )}
                </time>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default InspectionCycleHistory;
